import appStore from '@stores/appStore'
import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'

dayjs.extend(utc)

/**
 * Placeholder the API sends instead of a claimant's real contact details when the caller is an
 * offshore staff member. Must match `OffshoreMaskRedactor.MaskValue` on the backend.
 */
export const MASKED_VALUE = '***'

/**
 * Claimant fields hidden from offshore staff. Mirrors the properties marked with
 * `[OffshoreMasked]` on `ClientDto` / `ClientCreateUpdateDto`.
 *
 * `cityId` — the State selector — is deliberately absent: it stays visible here exactly as it does
 * on the backend, because it is too coarse to locate anyone.
 *
 * `postalAddress` has no input of its own in the UI, but it still arrives on `clientInfo` from the
 * API and would otherwise be posted straight back as `"***"`.
 */
export const MASKED_CLIENT_FIELDS = [
  'email',
  'telephone',
  'homePhone',
  'emergencyContactPhone',
  'emergencyContactEmail',
  'representativePhone',
  'representativeEmail',
  'address',
  'postalAddress',
  'suburb',
  'postcode',
] as const

/**
 * Tenant setting key for the Hide Claimant Personal Information sub-feature (must mirror the
 * backend constant `AppSettings.ClaimantPrivacy.HidePersonalInfoEnabled`).
 */
const SETTING_HIDE_CLAIMANT_INFO = 'ICS.ClaimantPrivacy.HidePersonalInfoEnabled'

/**
 * True when the current user must not see claimant contact details.
 *
 * Two conditions, matching `CaseDetailManager.ShouldHideClaimantPersonalInfoAsync` on the backend:
 *
 * 1. `appStore.ifOffshoreStaff()` — the tenant has `OffshoreStaffAccessControl` on, the user is
 *    STAFF or ADMIN_STAFF, and the user carries the offshore flag.
 * 2. The tenant turned the Hide Claimant Personal Information sub-feature on. A tenant can run the
 *    master feature for its attachment / access rules alone and leave claimant data readable.
 *
 * The backend masks the values regardless — this is purely so the UI disables the inputs and hides
 * actions that would be pointless or would fail, rather than showing an editable `***`.
 */
export function isClientContactMasked(): boolean {
  if (!appStore.ifOffshoreStaff()) {
    return false
  }

  return (
    (
      appStore.currentTenant?.settings?.[SETTING_HIDE_CLAIMANT_INFO] || ''
    ).toLowerCase() === 'true'
  )
}

/**
 * Removes the masked fields from a create/update payload when the current user is not allowed to
 * see them, so `***` is never posted back.
 *
 * Defence in depth: `OffshoreWriteGuard` on the backend already restores the stored values before
 * mapping, but dropping them here means the request never carries a meaningless value in the first
 * place.
 */
export function stripMaskedClientFields<T extends Record<string, any>>(
  payload: T
): T {
  if (!payload || !isClientContactMasked()) {
    return payload
  }

  const result = { ...payload }
  MASKED_CLIENT_FIELDS.forEach(field => {
    delete result[field]
  })

  return result
}

/**
 * Case fields holding the claimant's Date of Injury, under each of the names the modules give it.
 * Mirrors the properties marked `[OffshoreMasked(OffshoreMaskStyle.YearOnly)]` on the backend.
 *
 * `oldInjuriedDateNote` is the audit-trail copy of the previous value — the same date by another
 * name, so it is stripped alongside the current one.
 */
export const MASKED_DATE_OF_INJURY_FIELDS = [
  'injuriedDate',
  'injuriedDateNote',
  'oldInjuriedDateNote',
  'injuryDate',
  'dateOfInjury',
] as const

/**
 * Renders a Date of Injury an offshore user is not allowed to see in full: the day and month become
 * stars and only the year is left, in whatever date format the tenant uses — `**\/**\/1993` for
 * `DD/MM/YYYY`, `** *** 1993` for `DD MMM YYYY`.
 *
 * The API has already dropped the value to 1 January of its own year, so the real day and month are
 * gone before this runs; formatting it this way is what stops an offshore user from reading that
 * 1 January as the date the injury actually happened.
 *
 * Read in UTC for the same reason `appStore.formatDateIgnoreTimezone` does: the stored value is a
 * date, not a moment, and shifting it by a timezone could roll 1 January back into the year before.
 */
export function formatMaskedDateOfInjury(value?: string | Date): string {
  if (!value) {
    return ''
  }

  const format = appStore.currentUser?.dateFormat || 'DD/MM/YYYY'

  // Day and month tokens become stars of the same width; `*` is not a dayjs token, so it survives
  // formatting untouched and only the year is filled in.
  return dayjs
    .utc(value)
    .format(format.replace(/D+|M+/g, part => '*'.repeat(part.length)))
}

/**
 * Removes the Date of Injury from a case create/update payload when the current user is not allowed
 * to see it, so a placeholder is never posted back over the stored date.
 *
 * Defence in depth: `OffshoreWriteGuard` in `CaseClientDetailService.AddOrUpdateAsync` already
 * restores the stored value before mapping.
 */
export function stripMaskedDateOfInjuryFields<T extends Record<string, any>>(
  payload: T
): T {
  if (!payload || !isClientContactMasked()) {
    return payload
  }

  const result = { ...payload }
  MASKED_DATE_OF_INJURY_FIELDS.forEach(field => {
    delete result[field]
  })

  return result
}
